import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Stack, Typography } from "@mui/material";
import {
  Edit as EditIcon,
  MenuBook as JournalIcon,
} from "@mui/icons-material";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  FinancialSummarySection,
  GeneralInformationSection,
} from "@/features/sales-down-payments";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  ResponsiveToolbarButton,
} from "@/features/ui";

export const SalesDownPayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salesDownPayment, setSalesDownPayment] = useState(null);
  const [error, setError] = useState(null);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadSalesDownPayment = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`sales-down-payments/${referenceNumber}`, {
        params: {
          withBuyer: true,
          withSeller: true,
          withPaymentTerm: true,
          withSalesOrder: true,
          withAllocationHistory: true,
          withSalesPayments: true,
          withCreator: true,
        },
      });
      setSalesDownPayment(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat uang muka penjualan"),
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadSalesDownPayment();
  }, [loadSalesDownPayment]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.UPDATE_SALES_DOWN_PAYMENTS) && (
        <ResponsiveToolbarButton
          label="Ubah"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        />
      )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Uang Muka Penjualan"
      breadcrumbs={[
        {
          title: "Uang Muka Penjualan",
          path: "/sales/sales-down-payments",
        },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection salesDownPayment={salesDownPayment} />
        <FinancialSummarySection salesDownPayment={salesDownPayment} />
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="SALES_INVOICE"
          transactionableId={salesDownPayment?.id}
        />
      </Stack>
    </PageContainer>
  );
};
